import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/utils/helpers";

/**
 * The accent line under the open tab (library, Components / Navigation: "Tabs
 * keep the accent line, and it slides to the tab you pick in 200 ms"). Render
 * it as the last child of the tab list (which must be `position: relative`):
 * it measures the selected tab and moves on the snappy spring by transform, so
 * it can reverse mid-way. Under reduced motion the line jumps.
 */
export function KitTabIndicator({
  selectedKey,
  className,
}: {
  /** Changes when the selected tab changes; the line re-measures on it. */
  selectedKey: string | number | null | undefined;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [box, setBox] = useState<{ x: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const list = ref.current?.parentElement;
    if (!list) return;
    const measure = () => {
      const tab = list.querySelector<HTMLElement>(':scope > [role="tab"][aria-selected="true"]');
      setBox((previous) => {
        if (!tab) return null;
        const next = { x: tab.offsetLeft, width: tab.offsetWidth };
        return previous && previous.x === next.x && previous.width === next.width ? previous : next;
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [selectedKey]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute bottom-[-1px] left-0 h-0.5 bg-co-accent",
        "[transition:transform_var(--spring-snappy),width_var(--spring-snappy)] motion-reduce:transition-none",
        !box && "invisible",
        className,
      )}
      style={box ? { width: box.width, transform: `translateX(${box.x}px)` } : { width: 0 }}
    />
  );
}
